import React, { useState, useEffect } from 'react';
import { Truck, CheckCircle, XCircle } from 'lucide-react';
import { apiPublic, api } from '../../utils/api';

const WelcomePage = ({ onLogin }) => {
  const [status, setStatus] = useState('verifying'); // verifying, valid, invalid, logging_in, error
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (!token) {
      setStatus('invalid');
      setError('No invite token found');
      return;
    }
    
    // Verify the token
    verifyToken(token);
  }, []);
  
  const verifyToken = async (token) => {
    try {
      const data = await apiPublic(`/auth/verify-invite/${token}`);
      setInviteData({ ...data, token });
      setStatus('valid');
    } catch (err) {
      setStatus('invalid');
      setError(err.message || 'Invalid invite link');
    }
  };
  
  const handleLogin = async () => {
    if (!inviteData?.token) return;
    
    setStatus('logging_in');
    
    try {
      const data = await api(`/auth/redeem-invite/${inviteData.token}`, { method: 'POST' });
      
      // Store non-sensitive data only — tokens are in httpOnly cookies
      if (data.expires_at) {
        localStorage.setItem('lawd_expires_at', String(data.expires_at));
      }
      
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      
      onLogin(data.user);
    } catch (err) {
      setStatus('error');
      setError('Failed to complete login');
    }
  };
  
  const goToLogin = () => {
    window.history.replaceState({}, document.title, '/');
    window.location.reload();
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-emerald-500 rounded-2xl mb-3 sm:mb-4">
            <Truck className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">LAWD</h1>
          <p className="text-slate-400 mt-1 sm:mt-2 text-sm sm:text-base">Local Australian Work Diary</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-5 sm:p-8 border border-slate-700">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white text-sm sm:text-base">Verifying your invite link...</p>
            </div>
          )}
          
          {status === 'valid' && inviteData && (
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Welcome, {inviteData.first_name}!</h2>
              <p className="text-slate-400 mb-5 sm:mb-6 text-sm sm:text-base">
                You've been invited to join LAWD as a driver.
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mb-5 sm:mb-6">
                Email: {inviteData.email}
              </p>
              <button
                onClick={handleLogin}
                className="w-full py-2.5 sm:py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition text-sm sm:text-base"
                data-testid="welcome-continue-btn"
              >
                Continue to LAWD
              </button>
            </div>
          )}
          
          {status === 'logging_in' && (
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-white text-sm sm:text-base">Logging you in...</p>
            </div>
          )}
          
          {(status === 'invalid' || status === 'error') && (
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-7 h-7 sm:w-8 sm:h-8 text-red-500" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Invalid Link</h2>
              <p className="text-slate-400 mb-5 sm:mb-6 text-sm sm:text-base">{error}</p>
              <button
                onClick={goToLogin}
                className="w-full py-2.5 sm:py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition text-sm sm:text-base"
                data-testid="welcome-login-btn"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
